import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ExportCsvButton } from "../components/ExportCsvButton";
import { GeneralStatusBadge, GENERAL_STATUSES_ALL } from "../components/GeneralStatusBadge";
import { QuickFiltersBar } from "../components/QuickFiltersBar";
import { ActiveFilterChips } from "../components/ActiveFilterChips";
import { SalePeekPanel } from "../components/SalePeekPanel";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { TableSkeleton } from "../components/DashboardSkeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { AdvancedFilters, useAdvancedFilters, EMPTY_FILTERS } from "../components/AdvancedFilters";
import { activeFilterClass } from "../lib/filterStyles";
import {
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Users,
  User,
  FileText,
  X,
  SlidersHorizontal,
  Calendar,
  LayoutGrid,
  Rows3,
  Loader2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Luna comisionare = luna CSD (obiectivele se raportează după data CSD).
// csd_date vine ca "dd.mm.yyyy" (din listă) sau ISO — întoarce "mm.yyyy".
const MONTHS_RO = ["Ian","Feb","Mar","Apr","Mai","Iun","Iul","Aug","Sep","Oct","Nov","Dec"];
const commissionMonth = (csd) => {
  if (!csd) return null;
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(csd);
  if (m) return `${MONTHS_RO[parseInt(m[2], 10) - 1]} ${m[3]}`;
  const d = new Date(csd);
  return isNaN(d) ? null : `${MONTHS_RO[d.getMonth()]} ${d.getFullYear()}`;
};

// Varianta A — flag-urile secundare ale unei vânzări (badge overflow "+N" în tabel)
const buildSaleFlags = (sale) => {
  const flags = [];
  if (sale.needs_reconciliation) flags.push({ key: "needs-reconciliation", label: "⚠ Agent neasociat", cls: "bg-amber-50 text-amber-700 border border-amber-200", title: sale.reconciliation_note || "Codul de agent din import nu corespunde unui user CRM. Vezi Users → Agenți pentru asignare." });
  if (sale.duplicate_pod_flag) flags.push({ key: "duplicate-pod", label: "POD dup", cls: "bg-amber-100 text-amber-900", title: "POD duplicat detectat la import" });
  if (sale.bad_data_flag) flags.push({ key: "bad-data", label: "Date greșite", cls: "bg-rose-100 text-rose-900", title: "Date greșite raportate" });
  if (sale.customer_refusal_flag) flags.push({ key: "customer-refusal", label: "Refuz", cls: "bg-rose-100 text-rose-900", title: "Refuz client" });
  if (sale.problem_reason_code && !sale.customer_refusal_flag && !sale.bad_data_flag) flags.push({ key: "problem-reason", label: sale.problem_reason_code, cls: "bg-orange-100 text-orange-900", title: `Cod problemă: ${sale.problem_reason_code}` });
  if (sale.requires_rework) flags.push({ key: "rework", label: "Rework", cls: "bg-amber-100 text-amber-900", title: "Necesită refacere" });
  if (sale.returned) flags.push({ key: "returned", label: "↩️ Returnată", cls: "bg-amber-100 text-amber-900", title: "Returnată către agent" });
  if (sale.reconciliation_status && sale.reconciliation_status !== "ok" && !sale.needs_reconciliation) flags.push({ key: "recon-status", label: "⚠ Review", cls: "bg-rose-100 text-rose-900", title: `Status reconciliere: ${sale.reconciliation_status}` });
  return flags;
};

const Sales = () => {
  const { canEdit, canDelete, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [advancedFilters, setAdvancedFilters] = useAdvancedFilters("sales-advanced-filters-v1");
  const fetchSalesAbortRef = useRef(null);

  // Apply URL → filters once on mount (e.g., navigated from Dashboard widget).
  // IMPORTANT: When URL contains filter params, RESET to EMPTY defaults first,
  // discarding any persisted sessionStorage filters. This ensures Dashboard
  // widget links produce the intended filtered view regardless of prior state.
  const urlAppliedRef = useRef(false);
  useEffect(() => {
    if (urlAppliedRef.current) return;
    const filterKeys = [
      "status", "business_status", "supplier", "source_system", "customer_type",
      "agency_id", "agent_id", "product_id", "offer_id",
      "signed_date_from", "signed_date_to", "needs_reconciliation", "unassigned_only",
      "activation_days_min", "supplier_status", "supplier_ok", "csd_date_from", "csd_date_to",
      "document_validation_date_from", "document_validation_date_to",  // Iter52 P14j
      "request_confirmed_from", "request_confirmed_to", "sf_last_modified_by",  // Iter52 P14j
      "sort_by", "sort_dir",
      "requires_rework", "problem_reason_code",  // Iter34c
      "returned", "commissioned",  // Iter36
      "registration_type", "commissionable",  // Iter52 P13d
      "incomplete",  // Iter52 P13h — dashboard widget deep-link
    ];
    const incoming = {};
    filterKeys.forEach((k) => {
      const v = searchParams.get(k);
      if (v !== null && v !== "") incoming[k] = v;
    });
    // Faza B v2 — general_status may be repeated in URL (multi-select)
    const gsAll = searchParams.getAll("general_status").filter(Boolean);
    if (gsAll.length > 0) {
      incoming.general_status = gsAll;
    }
    // Faza B v2 — extra keys for is_stale + preset
    ["is_stale", "preset"].forEach((k) => {
      const v = searchParams.get(k);
      if (v !== null && v !== "") incoming[k] = v;
    });
    if (Object.keys(incoming).length > 0) {
      setAdvancedFilters({ ...EMPTY_FILTERS, ...incoming });
    }
    urlAppliedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Faza B v2 — Sync advancedFilters → URL (outbound), so deep-linking works both ways.
  // Runs only after initial hydration to avoid clobbering URL on mount.
  useEffect(() => {
    if (!urlAppliedRef.current) return;
    const next = new URLSearchParams();
    Object.entries(advancedFilters).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      if (k === "general_status" && Array.isArray(v)) {
        v.forEach((item) => { if (item) next.append("general_status", item); });
        return;
      }
      if (Array.isArray(v) && v.length === 0) return;
      next.set(k, v);
    });
    // Preserve unrelated search params (e.g. search, view-mode) — keep everything not tracked here.
    const filterKeys = new Set([
      "supplier", "activation_date_from", "activation_date_to", "billing_cycle",
      "amount_min", "amount_max", "kwh_min", "kwh_max", "smart_min", "smart_max",
      "created_from", "created_to", "requires_rework", "problem_reason_code",
      "returned", "commissioned", "sort_by", "sort_dir",
      "channel", "needs_reconciliation", "commercial_agent_id",
      "general_status", "general_status_rule_code", "has_conflict",
      "has_blocking_conflict", "is_stale", "preset",
    ]);
    // Keep non-filter params
    searchParams.forEach((val, key) => {
      if (!filterKeys.has(key)) next.set(key, val);
    });
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);
  
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0, filtered_count: 0 });
  const [selectedSales, setSelectedSales] = useState([]);
  // Iter53 P21 — Mobile: cards vs table toggle + append mode for infinite scroll
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("sales-view-mode");
      if (saved === "table" || saved === "cards") return saved;
      return typeof window !== "undefined" && window.innerWidth < 768 ? "cards" : "table";
    } catch { return "table"; }
  });
  const switchView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem("sales-view-mode", mode); } catch { /* ignore */ }
  };
  // Varianta A — filtre avansate colapsate implicit (persistat)
  const [showAdvanced, setShowAdvanced] = useState(() => {
    try { return localStorage.getItem("sales-advanced-open-v1") === "1"; } catch { return false; }
  });
  const toggleAdvanced = () => {
    setShowAdvanced((v) => {
      try { localStorage.setItem("sales-advanced-open-v1", v ? "0" : "1"); } catch { /* ignore */ }
      return !v;
    });
  };
  // Varianta A — peek panel triaj rapid
  const [peekSaleId, setPeekSaleId] = useState(null);
  // Iter54 fix — filtru "vânzările clientului" (deep-link din ClientDetail)
  const clientFilterId = searchParams.get("client_id") || "";
  const [clientFilterInfo, setClientFilterInfo] = useState(null);
  useEffect(() => {
    if (!clientFilterId) { setClientFilterInfo(null); return; }
    let cancelled = false;
    axios.get(`${API_URL}/clients/${clientFilterId}`)
      .then((r) => { if (!cancelled) setClientFilterInfo(r.data); })
      .catch(() => { if (!cancelled) setClientFilterInfo(null); });
    return () => { cancelled = true; };
  }, [clientFilterId]);
  const clearClientFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("client_id");
    setSearchParams(next, { replace: true });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };
  const appendModeRef = useRef(false);
  const [statuses, setStatuses] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [filterFields, setFilterFields] = useState([]);
  
  // Dynamic filters
  const [activeFilters, setActiveFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilterField, setSelectedFilterField] = useState("");
  const [filterValue, setFilterValue] = useState("");
  // Iter32 P4 — optional "Owner comercial" column (persistent in localStorage)
  const [showCommercialColumn, setShowCommercialColumn] = useState(() => {
    try { return localStorage.getItem("sales-show-commercial-column") === "1"; } catch { return false; }
  });
  const [agentsById, setAgentsById] = useState({});
  
  useEffect(() => {
    // Lazy-fetch agent name map only when the column is visible
    if (!showCommercialColumn || Object.keys(agentsById).length > 0) return;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/agents?limit=500`);
        const list = r.data?.data || r.data?.items || [];
        const map = {};
        list.forEach((a) => { map[a.id] = a; });
        setAgentsById(map);
      } catch (e) { /* silent */ }
    })();
  }, [showCommercialColumn, agentsById]);
  
  const toggleCommercialColumn = () => {
    const next = !showCommercialColumn;
    setShowCommercialColumn(next);
    try { localStorage.setItem("sales-show-commercial-column", next ? "1" : "0"); } catch { /* ignore */ }
  };
  
  // Date range filters
  const [dateFilters, setDateFilters] = useState({
    data_semnarii_from: "",
    data_semnarii_to: "",
    data_creare_from: "",
    data_creare_to: ""
  });

  // Filtru rapid „lună comisionare" (= luna CSD). Traduce în csd_date_from/to.
  const [commissionFilter, setCommissionFilter] = useState({ year: "", month: "" });
  const commissionRange = (() => {
    const { year, month } = commissionFilter;
    if (!year) return null;
    if (month && month !== "all") {
      const m = parseInt(month, 10);
      const last = new Date(parseInt(year, 10), m, 0).getDate();
      return { from: `${year}-${String(m).padStart(2, "0")}-01`, to: `${year}-${String(m).padStart(2, "0")}-${String(last).padStart(2, "0")}` };
    }
    return { from: `${year}-01-01`, to: `${year}-12-31` };
  })();
  
  // Dialogs
  const [bulkStatusDialog, setBulkStatusDialog] = useState(false);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [assignField, setAssignField] = useState("operatore_bo");
  const [assignValue, setAssignValue] = useState("");

  const buildListParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    // Iter54 fix — "Vezi vânzările" din fișa clientului (deep-link ?client_id=)
    if (clientFilterId) params.set("client_id", clientFilterId);

    // Phase 2 advanced filters — append non-empty
    Object.entries(advancedFilters).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      // Faza B v2 — general_status is an array (multi-select) → append repeat params
      if (k === "general_status" && Array.isArray(v)) {
        v.forEach((item) => {
          if (item) params.append("general_status", item);
        });
        return;
      }
      // Skip empty arrays generally
      if (Array.isArray(v) && v.length === 0) return;
      params.set(k, v);
    });

    // Convert activeFilters to JSON string for the API
    if (Object.keys(activeFilters).length > 0) {
      params.set("filters", JSON.stringify(activeFilters));
    }

    // Add date range filters
    if (dateFilters.data_semnarii_from) params.set("data_semnarii_from", dateFilters.data_semnarii_from);
    if (dateFilters.data_semnarii_to) params.set("data_semnarii_to", dateFilters.data_semnarii_to);
    if (dateFilters.data_creare_from) params.set("data_creare_from", dateFilters.data_creare_from);
    if (dateFilters.data_creare_to) params.set("data_creare_to", dateFilters.data_creare_to);

    // Filtru rapid lună comisionare → csd_date_from/to (are prioritate față de advancedFilters CSD)
    if (commissionRange) {
      params.set("csd_date_from", commissionRange.from);
      params.set("csd_date_to", commissionRange.to);
    }
    return params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, activeFilters, dateFilters, advancedFilters, clientFilterId, commissionFilter]);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();
    fetchSalesAbortRef.current?.abort();
    fetchSalesAbortRef.current = controller;
    try {
      const params = buildListParams();
      params.set("page", pagination.page);
      params.set("limit", pagination.limit);
      
      const response = await axios.get(`${API_URL}/sales?${params.toString()}`, { signal: controller.signal });
      // Iter53 P21 — append mode for mobile "Load more". Reset by default on filter/page-set changes.
      const shouldAppend = appendModeRef.current;
      appendModeRef.current = false;
      setSales((prev) => shouldAppend ? [...prev, ...response.data.data] : response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages,
        filtered_count: response.data.filtered_count
      }));
    } catch (error) {
      if (axios.isCancel(error) || error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        return; // ignore aborted requests
      }
      toast.error("Eroare la încărcarea vânzărilor");
    } finally {
      if (fetchSalesAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [pagination.page, pagination.limit, buildListParams]);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/statuses`);
      setStatuses(response.data);
    } catch (error) {
      console.error("Failed to fetch statuses");
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/agencies`);
      setAgencies(response.data);
    } catch (error) {
      console.error("Failed to fetch agencies");
    }
  };

  const fetchProductTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/products?active_only=true`);
      setProductTypes(response.data);
    } catch (error) {
      console.error("Failed to fetch product types");
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API_URL}/offers?active_only=true`);
      setOffers(response.data);
    } catch (error) {
      console.error("Failed to fetch offers");
    }
  };

  const fetchFilterFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/sales/fields`);
      setFilterFields(response.data.fields);
    } catch (error) {
      console.error("Failed to fetch filter fields");
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchStatuses();
      await fetchAgencies();
      await fetchProductTypes();
      await fetchOffers();
      await fetchFilterFields();
    };
    init();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchSales(), 0);
    return () => clearTimeout(t);
  }, [fetchSales]);

  const handleSearch = (e) => {
    e.preventDefault();
    setTimeout(() => setPagination(prev => ({ ...prev, page: 1 })), 0);
  };

  // Iter53 P21 — Mobile "Load more" — append next page to the existing list
  const handleLoadMore = () => {
    if (loading) return;
    if (pagination.page >= pagination.pages) return;
    appendModeRef.current = true;
    setPagination(prev => ({ ...prev, page: prev.page + 1 }));
  };

  const addFilter = () => {
    if (!selectedFilterField || !filterValue) return;
    setActiveFilters(prev => ({
      ...prev,
      [selectedFilterField]: filterValue
    }));
    setSelectedFilterField("");
    setFilterValue("");
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const removeFilter = (key) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    setDateFilters({
      data_semnarii_from: "",
      data_semnarii_to: "",
      data_creare_from: "",
      data_creare_to: ""
    });
    setSearchTerm("");
    setSearchParams({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  const handleDateFilterChange = (field, value) => {
    setDateFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSales(sales.map(s => s.id));
    } else {
      setSelectedSales([]);
    }
  };

  const handleSelectSale = (saleId, checked) => {
    if (checked) {
      setSelectedSales([...selectedSales, saleId]);
    } else {
      setSelectedSales(selectedSales.filter(id => id !== saleId));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!newStatus || selectedSales.length === 0) return;
    
    try {
      await axios.post(`${API_URL}/sales/bulk/status`, {
        sale_ids: selectedSales,
        new_status: newStatus
      });
      toast.success(`${selectedSales.length} vânzări actualizate`);
      setBulkStatusDialog(false);
      setSelectedSales([]);
      setNewStatus("");
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la actualizarea vânzărilor");
    }
  };

  const handleBulkAssign = async () => {
    if (!assignValue || selectedSales.length === 0) return;
    
    try {
      await axios.post(`${API_URL}/sales/bulk/assign`, {
        sale_ids: selectedSales,
        field: assignField,
        value: assignValue
      });
      toast.success(`${selectedSales.length} vânzări actualizate`);
      setBulkAssignDialog(false);
      setSelectedSales([]);
      setAssignValue("");
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la asignarea vânzărilor");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    
    try {
      await axios.delete(`${API_URL}/sales/${deleteDialog}`);
      toast.success("Vânzare ștearsă");
      setDeleteDialog(null);
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergerea vânzării");
    }
  };

  // Iter53 P41 — Bulk delete selected sales
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    if (selectedSales.length === 0) return;
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API_URL}/sales/bulk/delete`, { sale_ids: selectedSales });
      const deleted = r.data?.deleted || 0;
      const skipped = r.data?.skipped || 0;
      if (deleted > 0) {
        toast.success(`${deleted} vânzări șterse${skipped > 0 ? ` (${skipped} sărite din RBAC)` : ""}`);
      } else {
        toast.warning("Nicio vânzare ștearsă (verifică permisiunile)");
      }
      setSelectedSales([]);
      setBulkDeleteDialog(false);
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergere în masă");
    } finally {
      setBulkDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      "OK": "bg-emerald-50 text-emerald-700 border-emerald-200",
      "Activat": "bg-emerald-100 text-emerald-800 border-emerald-300",  // Iter52 P14d
      "În Activare": "bg-sky-50 text-sky-700 border-sky-200",
      "KO": "bg-red-50 text-red-700 border-red-200",
      "Draft": "bg-slate-100 text-slate-700 border-slate-200",
      "Trimis": "bg-blue-50 text-blue-700 border-blue-200",
      "În Procesare": "bg-amber-50 text-amber-700 border-amber-200",
      "Necesită Corecție": "bg-amber-50 text-amber-700 border-amber-200",
      "Anulat": "bg-gray-100 text-gray-700 border-gray-200"
    };
    return colors[status] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getStatusIcon = (status) => {
    if (status === "OK" || status === "Activat") return <CheckCircle2 className="h-3.5 w-3.5" />;
    if (status === "KO") return <XCircle className="h-3.5 w-3.5" />;
    return <Clock className="h-3.5 w-3.5" />;
  };

  const getFilterLabel = (key) => {
    const field = filterFields.find(f => f.key === key);
    return field ? field.label : key;
  };

  const getClientDisplay = (sale) => {
    if (!sale.client) return "-";
    if (sale.client.client_type === "PF") {
      return sale.client.full_name || `${sale.client.last_name || ""} ${sale.client.first_name || ""}`.trim() || "PF";
    }
    // Iter52 P13c — fallback chain prevents bare CUI display when legacy PJ
    // imports stored the company name in `full_name`/`last_name` instead of company_name.
    return sale.client.company_name || sale.client.full_name || sale.client.last_name || sale.client.cui || "PJ";
  };
  
  const hasActiveDateFilters = dateFilters.data_semnarii_from || dateFilters.data_semnarii_to || 
                               dateFilters.data_creare_from || dateFilters.data_creare_to;
  const totalActiveFilters = Object.keys(activeFilters).length + (hasActiveDateFilters ? 1 : 0);

  // Varianta A — count filtre avansate active (exclude sort + preset)
  const advCount = Object.entries(advancedFilters).filter(([k, v]) => {
    if (["sort_by", "sort_dir", "preset"].includes(k)) return false;
    if (Array.isArray(v)) return v.length > 0;
    return Boolean(v);
  }).length;

  // Varianta A — navigare peek panel între rândurile listei curente
  const peekIndex = peekSaleId ? sales.findIndex((s) => s.id === peekSaleId) : -1;
  const navigatePeek = useCallback((dir) => {
    setPeekSaleId((current) => {
      const idx = sales.findIndex((s) => s.id === current);
      const next = idx + dir;
      if (next >= 0 && next < sales.length) return sales[next].id;
      return current;
    });
  }, [sales]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Vânzări</h1>
          <p className="text-slate-500 mt-1">
            Gestionează vânzările și contractele de furnizare energie
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleCommercialColumn}
            className={showCommercialColumn ? "border-[#1e3a8a] text-[#1e3a8a]" : ""}
            data-testid="toggle-commercial-column"
            title="Afișează/ascunde coloana Owner comercial"
          >
            {showCommercialColumn ? "✓ " : ""}Owner comercial
          </Button>
          {/* Iter53 P40 — Universal CSV export: aceleași filtre ca lista (principale + avansate + căutare) */}
          <ExportCsvButton
            entity="sales"
            queryParams={buildListParams()}
          />
          {/* Iter68f — Export de control Salesforce (aceleași filtre/drepturi; identificatorii SF realmente stocați) */}
          <ExportCsvButton
            entity="sales_sf_control"
            queryParams={buildListParams()}
            label="Export control SF"
            testId="export-sf-control-btn"
          />
          <Link to="/sales/new">
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-sale-btn">
              <Plus className="h-4 w-4 mr-2" />
              Vânzare Nouă
            </Button>
          </Link>
        </div>
      </div>

      {/* Faza B v2 — Quick Filters Bar (preset-uri rapide Status General) */}
      <QuickFiltersBar
        activePreset={advancedFilters.preset || null}
        onSelect={(preset) => {
          setAdvancedFilters({ ...advancedFilters, preset: preset || "" });
          setPagination((prev) => ({ ...prev, page: 1 }));
        }}
      />

      {/* Varianta A — Filtre avansate colapsate implicit + chips active removabile */}
      <div className="hidden md:block space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleAdvanced}
            data-testid="toggle-advanced-filters"
            aria-expanded={showAdvanced}
            className={advCount > 0 ? "border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100" : ""}
          >
            <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
            Filtre avansate
            {advCount > 0 && (
              <Badge className="ml-1.5 bg-blue-600 hover:bg-blue-600 text-white" data-testid="adv-filter-count">
                {advCount}
              </Badge>
            )}
            <ChevronDown className={`h-3.5 w-3.5 ml-1.5 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
          </Button>
          <ActiveFilterChips
            filters={advancedFilters}
            onChange={(next) => {
              setAdvancedFilters(next);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            lookups={{ agencies, products: productTypes, offers }}
          />
        </div>
        {showAdvanced && (
          <AdvancedFilters
            value={advancedFilters}
            onChange={(next) => {
              setAdvancedFilters(next);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            userRole={user?.role}
            storageKey="sales-advanced-filters-v1"
            showActivationDays={true}
          />
        )}
      </div>

      {/* Iter34c — Active rework filter banner */}
      {(advancedFilters.requires_rework === "true" || advancedFilters.requires_rework === true) && (
        <div
          className="flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900"
          data-testid="rework-filter-banner"
        >
          <span>
            🔧 Vezi doar vânzări care necesită <strong>refacere</strong>
            {advancedFilters.problem_reason_code && (
              <span> · motiv: <code className="rounded bg-amber-100 px-1">{advancedFilters.problem_reason_code}</code></span>
            )}
          </span>
          <button
            type="button"
            onClick={() => {
              const next = { ...advancedFilters, requires_rework: "", problem_reason_code: "" };
              setAdvancedFilters(next);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="text-xs underline hover:no-underline"
            data-testid="clear-rework-filter"
          >
            Elimină filtru
          </button>
        </div>
      )}
      {/* Iter54 — banner filtru "vânzările clientului" (deep-link din fișa clientului) */}
      {clientFilterId && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5" data-testid="client-filter-banner">
          <div className="flex items-center gap-2 text-sm text-blue-900 min-w-0">
            <User className="h-4 w-4 shrink-0" />
            <span className="truncate">
              Vânzările clientului:{" "}
              <Link to={`/clients/${clientFilterId}`} className="font-semibold underline hover:no-underline" data-testid="client-filter-name">
                {clientFilterInfo
                  ? (clientFilterInfo.client_type === "PJ"
                      ? (clientFilterInfo.company_name || clientFilterInfo.full_name || clientFilterInfo.cui)
                      : (clientFilterInfo.full_name || `${clientFilterInfo.last_name || ""} ${clientFilterInfo.first_name || ""}`.trim() || clientFilterInfo.cnp))
                  : "..."}
              </Link>
              {pagination.filtered_count != null && (
                <span className="text-blue-600"> · {pagination.filtered_count ?? pagination.total} vânzări</span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={clearClientFilter}
            className="text-xs underline hover:no-underline text-blue-800 shrink-0"
            data-testid="clear-client-filter"
          >
            Afișează toate vânzările
          </button>
        </div>
      )}
      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Caută după nr. contract, POD, NLC..."
                className={`pl-10 ${activeFilterClass(searchTerm)}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters"
              className={totalActiveFilters > 0 ? "border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100" : ""}
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filtre
              {totalActiveFilters > 0 && (
                <Badge className="ml-2 bg-blue-600 hover:bg-blue-600 text-white">
                  {totalActiveFilters}
                </Badge>
              )}
            </Button>
            
            <Button type="submit" data-testid="search-btn">
              <Search className="h-4 w-4 mr-2" />
              Caută
            </Button>
          </form>

          {/* Filtru rapid: lună comisionare (= luna CSD, baza obiectivelor) */}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3" data-testid="commission-month-quickfilter">
            <span className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" /> Lună comision (CSD):
            </span>
            <Select
              value={commissionFilter.year || "none"}
              onValueChange={(v) => {
                setCommissionFilter(v === "none" ? { year: "", month: "" } : { ...commissionFilter, year: v });
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
              <SelectTrigger className={`h-8 w-28 ${commissionFilter.year ? activeFilterClass("x") : ""}`} data-testid="commission-year-select">
                <SelectValue placeholder="An" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Toți anii</SelectItem>
                {[2024, 2025, 2026, 2027].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={commissionFilter.month || "all"}
              disabled={!commissionFilter.year}
              onValueChange={(v) => {
                setCommissionFilter({ ...commissionFilter, month: v });
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            >
              <SelectTrigger className={`h-8 w-36 ${commissionFilter.year && commissionFilter.month && commissionFilter.month !== "all" ? activeFilterClass("x") : ""}`} data-testid="commission-month-select">
                <SelectValue placeholder="Luna" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tot anul</SelectItem>
                {MONTHS_RO.map((m, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {commissionFilter.year && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-slate-500"
                onClick={() => { setCommissionFilter({ year: "", month: "" }); setPagination((p) => ({ ...p, page: 1 })); }}
                data-testid="commission-filter-clear"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Resetează
              </Button>
            )}
          </div>
          
          {/* Expanded Filters — inline pe desktop, bottom-sheet pe mobil */}
          {showFilters && (
            <>
              {/* Mobile backdrop */}
              <div
                className="fixed inset-0 z-40 bg-black/50 md:hidden"
                onClick={() => setShowFilters(false)}
                data-testid="mobile-filters-backdrop"
                aria-hidden="true"
              />
              <div
                className="fixed inset-x-0 bottom-0 z-50 max-h-[88vh] overflow-y-auto rounded-t-2xl border-t border-slate-200 bg-white p-4 shadow-2xl
                           md:static md:z-auto md:mt-6 md:max-h-none md:overflow-visible md:rounded-none md:border-0 md:border-t md:bg-transparent md:p-0 md:pt-6 md:shadow-none
                           space-y-6"
                data-testid="mobile-filters-sheet"
              >
                {/* Sheet handle + close (mobile only) */}
                <div className="-mt-1 mb-2 flex items-center justify-between md:hidden">
                  <h3 className="text-sm font-semibold text-slate-800">Filtre & Date</h3>
                  <button
                    type="button"
                    onClick={() => setShowFilters(false)}
                    className="rounded p-1 hover:bg-slate-100"
                    data-testid="mobile-filters-close"
                    aria-label="Închide filtrele"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              {/* Date Range Filters */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  <h3 className="font-medium text-slate-700">Filtrare după Date</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Data Semnarii Range */}
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <Label className="text-sm font-medium">Data Semnării</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">De la</Label>
                        <Input
                          type="text"
                          placeholder="DD.MM.YYYY"
                          value={dateFilters.data_semnarii_from}
                          onChange={(e) => handleDateFilterChange("data_semnarii_from", e.target.value)}
                          data-testid="data_semnarii_from"
                          className={activeFilterClass(dateFilters.data_semnarii_from)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Până la</Label>
                        <Input
                          type="text"
                          placeholder="DD.MM.YYYY"
                          value={dateFilters.data_semnarii_to}
                          onChange={(e) => handleDateFilterChange("data_semnarii_to", e.target.value)}
                          data-testid="data_semnarii_to"
                          className={activeFilterClass(dateFilters.data_semnarii_to)}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Data Creare Range */}
                  <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                    <Label className="text-sm font-medium">Data Creare</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">De la</Label>
                        <Input
                          type="text"
                          placeholder="DD.MM.YYYY"
                          value={dateFilters.data_creare_from}
                          onChange={(e) => handleDateFilterChange("data_creare_from", e.target.value)}
                          data-testid="data_creare_from"
                          className={activeFilterClass(dateFilters.data_creare_from)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Până la</Label>
                        <Input
                          type="text"
                          placeholder="DD.MM.YYYY"
                          value={dateFilters.data_creare_to}
                          onChange={(e) => handleDateFilterChange("data_creare_to", e.target.value)}
                          data-testid="data_creare_to"
                          className={activeFilterClass(dateFilters.data_creare_to)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Dynamic Filters */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                  <h3 className="font-medium text-slate-700">Filtre Dinamice</h3>
                </div>
                
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm text-slate-600">Câmp</Label>
                    <Select value={selectedFilterField} onValueChange={setSelectedFilterField}>
                      <SelectTrigger className="w-48" data-testid="filter-field-select">
                        <SelectValue placeholder="Selectează câmp" />
                      </SelectTrigger>
                      <SelectContent>
                        {filterFields.filter(f => f.type !== 'date').map((field) => (
                          <SelectItem key={field.key} value={field.key}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label className="text-sm text-slate-600">Valoare</Label>
                    {selectedFilterField === "status_ppc" || selectedFilterField === "status_sf" ? (
                      <Select value={filterValue} onValueChange={setFilterValue}>
                        <SelectTrigger className="w-48" data-testid="filter-value-select">
                          <SelectValue placeholder="Selectează status" />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map((status) => (
                            <SelectItem key={status.id} value={status.name}>
                              {status.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : selectedFilterField === "agency_id" ? (
                      <Select value={filterValue} onValueChange={setFilterValue}>
                        <SelectTrigger className="w-48" data-testid="filter-value-select">
                          <SelectValue placeholder="Selectează agenție" />
                        </SelectTrigger>
                        <SelectContent>
                          {agencies.map((agency) => (
                            <SelectItem key={agency.id} value={agency.id}>
                              {agency.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : selectedFilterField === "product_type_id" ? (
                      <Select value={filterValue} onValueChange={setFilterValue}>
                        <SelectTrigger className="w-48" data-testid="filter-value-select">
                          <SelectValue placeholder="Selectează produs" />
                        </SelectTrigger>
                        <SelectContent>
                          {productTypes.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : selectedFilterField === "offer_id" ? (
                      <Select value={filterValue} onValueChange={setFilterValue}>
                        <SelectTrigger className="w-48" data-testid="filter-value-select">
                          <SelectValue placeholder="Selectează ofertă" />
                        </SelectTrigger>
                        <SelectContent>
                          {offers.map((offer) => (
                            <SelectItem key={offer.id} value={offer.id}>
                              {offer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder="Introdu valoare"
                        className="w-48"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        data-testid="filter-value-input"
                      />
                    )}
                  </div>
                  
                  <Button
                    type="button"
                    onClick={addFilter}
                    disabled={!selectedFilterField || !filterValue}
                    data-testid="add-filter-btn"
                  >
                    Adaugă Filtru
                  </Button>
                </div>
              </div>
              
              {/* Active Filters */}
              {(Object.keys(activeFilters).length > 0 || hasActiveDateFilters) && (
                <div className="flex flex-wrap items-center gap-2 pt-4 border-t">
                  <span className="text-sm text-slate-500">Filtre active:</span>
                  {Object.entries(activeFilters).map(([key, value]) => (
                    <Badge
                      key={key}
                      variant="secondary"
                      className="flex items-center gap-1 px-3 py-1"
                    >
                      <span className="text-slate-600">{getFilterLabel(key)}:</span>
                      <span className="font-medium">{value}</span>
                      <button
                        onClick={() => removeFilter(key)}
                        className="ml-1 hover:text-red-600"
                        data-testid={`remove-filter-${key}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {hasActiveDateFilters && (
                    <Badge variant="secondary" className="flex items-center gap-1 px-3 py-1">
                      <Calendar className="h-3 w-3" />
                      <span>Filtre date active</span>
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="clear-all-filters"
                  >
                    Șterge toate
                  </Button>
                </div>
              )}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Results Info & Bulk Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="text-sm text-slate-600">
            <span className="font-medium" data-testid="sales-total">{pagination.total}</span> vânzări găsite
            {pagination.filtered_count !== pagination.total && (
              <span> (din {pagination.total} total)</span>
            )}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchSales}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Actualizează
          </Button>
          {/* Iter53 P21 — View toggle (vizibil pe mobil) */}
          <div
            className="ml-auto inline-flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5 md:hidden"
            data-testid="sales-view-toggle"
          >
            <button
              type="button"
              onClick={() => switchView("cards")}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
                viewMode === "cards"
                  ? "bg-[#1e3a8a] text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="view-mode-cards"
              aria-pressed={viewMode === "cards"}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Carduri
            </button>
            <button
              type="button"
              onClick={() => switchView("table")}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
                viewMode === "table"
                  ? "bg-[#1e3a8a] text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="view-mode-table"
              aria-pressed={viewMode === "table"}
            >
              <Rows3 className="h-3.5 w-3.5" />
              Tabel
            </button>
          </div>
        </div>
        
        {selectedSales.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">
              {selectedSales.length} selectate
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkStatusDialog(true)}
              data-testid="bulk-status-btn"
            >
              <FileText className="h-4 w-4 mr-1" />
              Schimbă Status
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkAssignDialog(true)}
              data-testid="bulk-assign-btn"
            >
              <Users className="h-4 w-4 mr-1" />
              Asignează
            </Button>
            {/* Iter53 P41 — Bulk export selectate */}
            <ExportCsvButton
              entity="sales"
              filters={{ sale_ids: selectedSales.join(",") }}
              label={`Export CSV (${selectedSales.length})`}
              testId="bulk-export-csv-btn"
            />
            {/* Iter53 P41 — Bulk delete (admin/backoffice only) */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkDeleteDialog(true)}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Șterge
            </Button>
          </div>
        )}
      </div>

      {/* Varianta A — Peek panel triaj rapid (navigare ↑/↓) */}
      <SalePeekPanel
        saleId={peekSaleId}
        onClose={() => setPeekSaleId(null)}
        onNavigate={navigatePeek}
        hasPrev={peekIndex > 0}
        hasNext={peekIndex >= 0 && peekIndex < sales.length - 1}
        position={{ index: peekIndex, total: sales.length }}
        canEdit={canEdit()}
      />

      {/* Sales Table */}
      <Card>
        <CardContent className="p-0">
          {loading && sales.length === 0 ? (
            <TableSkeleton rows={8} columns={9} />
          ) : sales.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500" data-testid="sales-empty-state">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p className="font-medium">Nu există vânzări</p>
              <p className="text-xs text-slate-400 mt-1">
                {searchTerm || Object.keys(activeFilters || {}).length || Object.keys(advancedFilters || {}).length
                  ? "Niciun rezultat pentru filtrele aplicate"
                  : "Niciun rezultat încă"}
              </p>
              <Link to="/sales/new" className="mt-4">
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Adaugă prima vânzare
                </Button>
              </Link>
            </div>
          ) : (
            <>
            {/* Table view — visible when viewMode=="table" (default on desktop) */}
            <div className={`transition-opacity duration-200 ${loading ? "opacity-50 pointer-events-none" : ""} ${viewMode === "table" ? "overflow-x-auto" : "hidden md:block overflow-x-auto"}`}>
              <table className="w-full" data-testid="sales-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <Checkbox
                        checked={selectedSales.length === sales.length && sales.length > 0}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Nr. Contract
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Produs
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Agenție
                    </th>
                    {showCommercialColumn && (
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
                        data-testid="commercial-column-header"
                      >
                        Owner comercial
                      </th>
                    )}
                    {/* Iter53 P50 — Status general (Faza B UI); Varianta A: primează înaintea statusului intern */}
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-testid="general-status-column-header">
                      Status general
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                    {/* Iter52 P13 — SF "Stare contract" (col V), decides commission */}
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-testid="sf-state-column-header">
                      Status SF
                    </th>
                    {/* Iter52 P13d — Tip proces (SF col J) for commission reporting */}
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-testid="registration-type-column-header">
                      Tip proces
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Data Semnării
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-testid="csd-date-column-header">
                      Data CSD
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-testid="commission-month-column-header" title="Luna în care vânzarea este luată în calcul la obiectiv (= luna CSD)">
                      Lună comision
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Data Creare
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Acțiuni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {sales.map((sale) => (
                    <tr
                      key={sale.id}
                      onClick={(e) => {
                        // Varianta A — click pe rând deschide peek panel; ignoră click pe elemente interactive
                        if (e.target.closest('a,button,input,label,[role="checkbox"],[role="menuitem"]')) return;
                        setPeekSaleId(sale.id);
                      }}
                      className={`cursor-pointer hover:bg-slate-50/50 transition-colors ${
                        selectedSales.includes(sale.id) ? "bg-sky-50" : peekSaleId === sale.id ? "bg-blue-50/70" : ""
                      }`}
                      data-testid={`sale-row-${sale.id}`}
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={selectedSales.includes(sale.id)}
                          onCheckedChange={(checked) => handleSelectSale(sale.id, checked)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/sales/${sale.id}`}
                          className="font-medium text-slate-900 hover:text-sky-600"
                        >
                          {/* Iter53 P29 — Fallback chain pentru identificator contract:
                              1. contract_number (SF number) → ideal
                              2. sf_contract_id (UUID SF)
                              3. easy_contract_id (Easy App) cu badge „Easy"
                              4. POD (last 6 chars) cu badge „POD"
                              5. „⏳ În așteptare SF" pentru cazul în care nu există nimic */}
                          {sale.contract?.contract_number ? (
                            <span data-testid={`contract-number-${sale.id}`}>
                              {sale.contract.contract_number}
                            </span>
                          ) : sale.contract?.sf_contract_id ? (
                            <span data-testid={`sf-contract-id-${sale.id}`} className="font-mono text-xs">
                              SF: {sale.contract.sf_contract_id}
                            </span>
                          ) : sale.contract?.easy_contract_id ? (
                            <span
                              data-testid={`easy-contract-id-${sale.id}`}
                              className="inline-flex items-center gap-1 font-mono text-xs"
                              title={`Easy App ID: ${sale.contract.easy_contract_id}`}
                            >
                              <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sky-100 text-sky-800 border border-sky-200">Easy</span>
                              {sale.contract.easy_contract_id.length > 14
                                ? `…${sale.contract.easy_contract_id.slice(-10)}`
                                : sale.contract.easy_contract_id}
                            </span>
                          ) : sale.contract?.pod ? (
                            <span
                              data-testid={`pod-fallback-${sale.id}`}
                              className="inline-flex items-center gap-1 font-mono text-xs"
                              title={`POD: ${sale.contract.pod} (fără număr contract încă)`}
                            >
                              <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">POD</span>
                              …{sale.contract.pod.slice(-8)}
                            </span>
                          ) : (
                            <span
                              data-testid={`awaiting-sf-${sale.id}`}
                              title={
                                sale.crm_internal_id
                                  ? `Dosar SF: ${sale.crm_internal_id} — fără nr. contract atribuit încă`
                                  : "Vânzarea nu are încă număr de contract atribuit de PPC/Salesforce"
                              }
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200"
                            >
                              ⏳ În așteptare SF
                            </span>
                          )}
                        </Link>
                        {/* Varianta A — flag overflow: primul badge + "+N" cu tooltip pentru restul */}
                        {(() => {
                          const flags = buildSaleFlags(sale);
                          if (flags.length === 0) return null;
                          const first = flags[0];
                          const rest = flags.slice(1);
                          return (
                            <span className="inline-flex items-center gap-1 ml-2 align-middle">
                              <Badge
                                className={`${first.cls} text-[10px] py-0 px-1.5 cursor-help`}
                                data-testid={`${first.key}-${sale.id}`}
                                title={first.title}
                              >
                                {first.label}
                              </Badge>
                              {rest.length > 0 && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 cursor-help"
                                  data-testid={`flags-overflow-${sale.id}`}
                                  title={rest.map((f) => `${f.label}${f.title ? ` — ${f.title}` : ""}`).join("\n")}
                                >
                                  +{rest.length}
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="font-medium text-slate-700">
                            {getClientDisplay(sale)}
                          </p>
                          <p className="text-slate-500">
                            {sale.client?.county || "-"}, {sale.client?.locality || "-"}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="text-slate-700">{sale.contract?.product_name || "-"}</p>
                          {/* Iter52 P14f — afișează categoria CANONICĂ a ofertei (EE / Gaz / VAS / HVAS / Combo EE+VAS / Combo Gaz+VAS).
                              Anterior afișa doar `commodity` ("electric"/"gas") care e utility-ul fizic — NU spunea dacă oferta e Combo. */}
                          {(() => {
                            // Iter52 P14h — normalize legacy lowercase forms ("ee" → "EE") from offers table.
                            const _ALIAS = { ee: "EE", gaz: "Gaz", vas: "VAS", hvas: "HVAS", combo_ee_vas: "Combo EE + VAS", combo_gaz_vas: "Combo Gaz + VAS" };
                            const rawK = sale.contract?.product_kind;
                            const k = rawK ? (_ALIAS[String(rawK).toLowerCase()] || rawK) : null;
                            const commodity = sale.contract?.commodity;
                            if (!k) {
                              return commodity ? (
                                <p className="text-slate-500 text-xs">{commodity}</p>
                              ) : null;
                            }
                            const cls =
                              k === "Combo EE + VAS"  ? "bg-gradient-to-r from-amber-100 to-indigo-100 text-amber-900 border-amber-200" :
                              k === "Combo Gaz + VAS" ? "bg-gradient-to-r from-orange-100 to-indigo-100 text-orange-900 border-orange-200" :
                              k === "EE"              ? "bg-amber-100 text-amber-800 border-amber-200" :
                              k === "Gaz"             ? "bg-orange-100 text-orange-800 border-orange-200" :
                              k === "VAS"             ? "bg-indigo-100 text-indigo-800 border-indigo-200" :
                              k === "HVAS"            ? "bg-purple-100 text-purple-800 border-purple-200" :
                                                        "bg-slate-100 text-slate-700 border-slate-200";
                            return (
                              <span
                                data-testid={`product-kind-${sale.id}`}
                                title={`Categorie canonică: ${k}${commodity ? ` · utility: ${commodity}` : ""}`}
                                className={`mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${cls}`}
                              >
                                {k}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {sale.agency?.name || "-"}
                      </td>
                      {showCommercialColumn && (
                        <td
                          className="px-4 py-3 text-sm"
                          data-testid={`commercial-cell-${sale.id}`}
                        >
                          {(() => {
                            const commId = sale.commercial_agent_id;
                            const sfId = sale.sf_agent_ref_id;
                            const comm = commId ? agentsById[commId] : null;
                            const sf = sfId ? agentsById[sfId] : null;
                            if (!commId) {
                              return <span className="italic text-slate-400">—</span>;
                            }
                            const commLabel = comm
                              ? (comm.full_name || "Agent fără nume")
                              : `${commId.slice(0, 8)}…`;
                            const mismatch = sfId && commId && sfId !== commId;
                            const title = mismatch && sf
                              ? `SF Owner diferit: ${sf.full_name || sf.id}`
                              : "Owner comercial CRM";
                            return (
                              <span title={title} className="inline-flex items-center gap-1">
                                <span className="font-medium text-slate-800">{commLabel}</span>
                                {comm?.primary_marca_code && (
                                  <span className="text-xs text-slate-500">· {comm.primary_marca_code}</span>
                                )}
                                {mismatch && (
                                  <span
                                    className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase text-amber-800"
                                    data-testid={`sf-mismatch-${sale.id}`}
                                  >
                                    ≠SF
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      {/* Iter53 P50 — Status general column (Varianta A: primează) */}
                      <td className="px-4 py-3">
                        <GeneralStatusBadge sale={sale} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${getStatusColor(sale.status)} border`} title="Status intern (operațional) — diferit de Status General, care e calculat automat din toate sursele">
                          {getStatusIcon(sale.status)}
                          <span className="ml-1">Intern: {sale.status}</span>
                        </Badge>
                      </td>
                      {/* Iter52 P13 — Salesforce "Stare contract" badge */}
                      <td className="px-4 py-3" data-testid={`sf-state-${sale.id}`}>
                        {(() => {
                          const s = sale.salesforce_contract_state;
                          if (!s) {
                            return <span className="text-xs text-slate-400 italic">—</span>;
                          }
                          const cls =
                            s === "Activat"    ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                            s === "Signed"     ? "bg-blue-100 text-blue-800 border-blue-300" :
                            s === "Terminated" ? "bg-rose-100 text-rose-800 border-rose-300" :
                                                 "bg-slate-100 text-slate-700 border-slate-300";
                          return (
                            <div className="flex flex-col items-start gap-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>
                                {s}
                              </span>
                              {/* Iter52 P13b — "comisionabil" hint for Activat (commission-eligible).
                                  P14o: ProductChange/Transfer sunt necomisionabile chiar dacă Activat. */}
                              {s === "Activat" && sale.is_commissionable && (
                                <span
                                  data-testid={`sf-commission-${sale.id}`}
                                  title="Contract activ — eligibil pentru raportul de comisionare"
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                                >
                                  💰 comisionabil
                                </span>
                              )}
                              {s === "Activat" && sale.is_commissionable === false && (
                                <span
                                  data-testid={`sf-non-commission-${sale.id}`}
                                  title="Proces ne-comisionabil (Product Change / Transfer Contract)"
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-300"
                                >
                                  ⊘ ne-comisionabil
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      {/* Iter52 P13d — Tip proces (SF Tip înregistrare caz) with commissionable indicator */}
                      <td className="px-4 py-3" data-testid={`reg-type-${sale.id}`}>
                        <div className="flex flex-col items-start gap-1">
                          {sale.case_record_type ? (
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                                sale.is_commissionable
                                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                  : "bg-slate-100 text-slate-700 border-slate-300"
                              }`}
                              title={sale.is_commissionable
                                ? `${sale.case_record_type} — comisionabil`
                                : `${sale.case_record_type} — ne-comisionabil`}
                            >
                              {sale.is_commissionable ? "💰" : "⊘"} {sale.case_record_type}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">—</span>
                          )}
                          {/* Iter52 P13h — needs classification warning */}
                          {sale.needs_classification && (
                            <span
                              data-testid={`needs-class-${sale.id}`}
                              title="Vânzarea nu are tip de produs sau ofertă setată. Completează clasificarea."
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
                            >
                              📋 incompletă
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {sale.contract?.signed_date || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700" data-testid={`csd-date-${sale.id}`}>
                        {sale.contract?.csd_date || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm" data-testid={`commission-month-${sale.id}`}>
                        {commissionMonth(sale.contract?.csd_date) ? (
                          <span className="inline-flex items-center rounded bg-indigo-50 text-indigo-700 px-2 py-0.5 text-xs font-medium">
                            {commissionMonth(sale.contract?.csd_date)}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {sale.created_at ? new Date(sale.created_at).toLocaleDateString('ro-RO') : "-"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`actions-${sale.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/sales/${sale.id}`}>
                                <Eye className="h-4 w-4 mr-2" />
                                Vizualizează
                              </Link>
                            </DropdownMenuItem>
                            {canEdit() && (
                              <DropdownMenuItem asChild>
                                <Link to={`/sales/${sale.id}?edit=1`}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editează
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {canDelete() && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-600"
                                  onClick={() => setDeleteDialog(sale.id)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Șterge
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Iter53 P21 — Mobile card view (visible when viewMode=="cards" on mobile) */}
            <div
              className={
                viewMode === "cards"
                  ? "flex flex-col gap-3 p-3 md:hidden"
                  : "hidden"
              }
              data-testid="sales-card-list"
            >
              {sales.map((sale) => {
                const sfState = sale.salesforce_contract_state;
                const sfCls =
                  sfState === "Activat"    ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                  sfState === "Signed"     ? "bg-blue-100 text-blue-800 border-blue-300" :
                  sfState === "Terminated" ? "bg-rose-100 text-rose-800 border-rose-300" :
                                             "bg-slate-100 text-slate-700 border-slate-300";
                const _ALIAS = { ee: "EE", gaz: "Gaz", vas: "VAS", hvas: "HVAS", combo_ee_vas: "Combo EE + VAS", combo_gaz_vas: "Combo Gaz + VAS" };
                const rawK = sale.contract?.product_kind;
                const k = rawK ? (_ALIAS[String(rawK).toLowerCase()] || rawK) : null;
                return (
                  <div
                    key={sale.id}
                    className={`rounded-lg border bg-white p-3 shadow-sm ${
                      selectedSales.includes(sale.id) ? "border-[#1e3a8a] bg-sky-50/50" : "border-slate-200"
                    }`}
                    data-testid={`sale-card-${sale.id}`}
                  >
                    {/* Header row: checkbox + contract # + actions */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <Checkbox
                          checked={selectedSales.includes(sale.id)}
                          onCheckedChange={(checked) => handleSelectSale(sale.id, checked)}
                          data-testid={`select-card-${sale.id}`}
                        />
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/sales/${sale.id}`}
                            className="block text-sm font-semibold text-[#1e3a8a] truncate"
                          >
                            {/* Iter53 P29 — same fallback chain ca în tabel pentru consistență */}
                            {sale.contract?.contract_number
                              || (sale.contract?.sf_contract_id ? `SF: ${sale.contract.sf_contract_id}` : null)
                              || (sale.contract?.easy_contract_id ? `Easy: …${sale.contract.easy_contract_id.slice(-8)}` : null)
                              || (sale.contract?.pod ? `POD: …${sale.contract.pod.slice(-8)}` : null)
                              || "⏳ În așteptare SF"}
                          </Link>
                          <p className="text-xs text-slate-700 truncate mt-0.5">
                            {getClientDisplay(sale)}
                          </p>
                          <p className="text-[11px] text-slate-500 truncate">
                            {sale.client?.county || "-"}{sale.client?.locality ? `, ${sale.client.locality}` : ""}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" data-testid={`actions-card-${sale.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/sales/${sale.id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              Vizualizează
                            </Link>
                          </DropdownMenuItem>
                          {canEdit() && (
                            <DropdownMenuItem asChild>
                              <Link to={`/sales/${sale.id}?edit=1`}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editează
                              </Link>
                            </DropdownMenuItem>
                          )}
                          {canDelete() && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => setDeleteDialog(sale.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Șterge
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Product + agency */}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600">
                      {sale.contract?.product_name && (
                        <span className="truncate max-w-[60%]" title={sale.contract.product_name}>
                          {sale.contract.product_name}
                        </span>
                      )}
                      {k && (
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                            k === "Combo EE + VAS"  ? "bg-gradient-to-r from-amber-100 to-indigo-100 text-amber-900 border-amber-200" :
                            k === "Combo Gaz + VAS" ? "bg-gradient-to-r from-orange-100 to-indigo-100 text-orange-900 border-orange-200" :
                            k === "EE"              ? "bg-amber-100 text-amber-800 border-amber-200" :
                            k === "Gaz"             ? "bg-orange-100 text-orange-800 border-orange-200" :
                            k === "VAS"             ? "bg-indigo-100 text-indigo-800 border-indigo-200" :
                            k === "HVAS"            ? "bg-purple-100 text-purple-800 border-purple-200" :
                                                       "bg-slate-100 text-slate-700 border-slate-200"
                          }`}
                        >
                          {k}
                        </span>
                      )}
                      {sale.agency?.name && (
                        <span className="text-slate-500">· {sale.agency.name}</span>
                      )}
                    </div>

                    {/* Status badges row */}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <Badge className={`${getStatusColor(sale.status)} border text-[10px] py-0`} title="Status intern (operațional) — diferit de Status General, care e calculat automat din toate sursele">
                        {getStatusIcon(sale.status)}
                        <span className="ml-1">Intern: {sale.status}</span>
                      </Badge>
                      {sfState && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${sfCls}`}>
                          SF · {sfState}
                        </span>
                      )}
                      {sale.is_commissionable && sfState === "Activat" && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          💰
                        </span>
                      )}
                      {sale.requires_rework && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-900 border border-amber-200">
                          Rework
                        </span>
                      )}
                      {sale.needs_classification && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                          📋 incompletă
                        </span>
                      )}
                      {sale.needs_reconciliation && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          ⚠ Agent neasociat
                        </span>
                      )}
                    </div>

                    {/* Footer: dates */}
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
                      <span>Semnat: {sale.contract?.signed_date || "—"}</span>
                      <span>Creat: {sale.created_at ? new Date(sale.created_at).toLocaleDateString("ro-RO") : "—"}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[10px]">
                      <span className="text-slate-500">CSD: {sale.contract?.csd_date || "—"}</span>
                      {commissionMonth(sale.contract?.csd_date) && (
                        <span className="inline-flex items-center rounded bg-indigo-50 text-indigo-700 px-1.5 py-0.5 font-medium">
                          Comision: {commissionMonth(sale.contract?.csd_date)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination — desktop only */}
      {pagination.pages > 1 && (
        <div className="hidden md:flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Afișare {((pagination.page - 1) * pagination.limit) + 1} -{" "}
            {Math.min(pagination.page * pagination.limit, pagination.total)} din{" "}
            {pagination.total} vânzări
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              data-testid="prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-700 px-2">
              Pagina {pagination.page} din {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              data-testid="next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Iter53 P21 — Mobile "Load more" — apare doar pe mobil în card view */}
      {viewMode === "cards" && pagination.page < pagination.pages && (
        <div className="md:hidden flex flex-col items-center gap-1 pb-2">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={loading}
            className="w-full"
            data-testid="mobile-load-more"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Încarcă mai multe ({Math.max(pagination.total - sales.length, 0)} rămase)
          </Button>
          <p className="text-[11px] text-slate-400">
            Pagina {pagination.page} din {pagination.pages}
          </p>
        </div>
      )}

      {/* Bulk Status Dialog */}
      <Dialog open={bulkStatusDialog} onOpenChange={setBulkStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schimbă Status</DialogTitle>
            <DialogDescription>
              Actualizează statusul pentru {selectedSales.length} vânzări selectate
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="bulk-status-select">
                <SelectValue placeholder="Selectează noul status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.id} value={status.name}>
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusDialog(false)}>
              Anulează
            </Button>
            <Button onClick={handleBulkStatusUpdate} disabled={!newStatus} data-testid="confirm-bulk-status">
              Actualizează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignDialog} onOpenChange={setBulkAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignează Vânzări</DialogTitle>
            <DialogDescription>
              Asignează {selectedSales.length} vânzări către un operator sau agenție
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Câmp</label>
              <Select value={assignField} onValueChange={setAssignField}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operatore_bo">Operator BO</SelectItem>
                  <SelectItem value="nume_agent">Agent</SelectItem>
                  <SelectItem value="agency_id">Agenție</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                {assignField === "agency_id" ? "Agenție" : "Nume"}
              </label>
              {assignField === "agency_id" ? (
                <Select value={assignValue} onValueChange={setAssignValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selectează agenție" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>
                        {agency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Introduceți numele"
                  value={assignValue}
                  onChange={(e) => setAssignValue(e.target.value)}
                  data-testid="assign-value-input"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignDialog(false)}>
              Anulează
            </Button>
            <Button onClick={handleBulkAssign} disabled={!assignValue} data-testid="confirm-assign">
              Asignează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare Ștergere</DialogTitle>
            <DialogDescription>
              Ești sigur că vrei să ștergi această vânzare? Această acțiune nu poate fi anulată.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              Anulează
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Șterge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter53 P41 — Bulk delete confirmation */}
      <Dialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ștergere în masă — {selectedSales.length} vânzări</DialogTitle>
            <DialogDescription>
              Ești sigur că vrei să ștergi <strong>{selectedSales.length}</strong> vânzări selectate?
              Doar rândurile Sale sunt șterse — clienții și contractele rămân intacte.
              Această acțiune <strong>NU poate fi anulată</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialog(false)} data-testid="bulk-delete-cancel">
              Anulează
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-confirm"
            >
              {bulkDeleting ? "Se șterge…" : `Șterge ${selectedSales.length} vânzări`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sales;
